import React, { lazy } from 'react';
import { Route, Routes } from 'react-router-dom';
import BaseLayout from './BaseLayout';
import { ROUTES_PRIVATE, ROUTES_SUPER_ADMIN } from '../../utils/routes';
import { getLocalStorageValue } from 'utils/helpers';
import { USER_ROLE } from 'utils/enums';

interface IProps {}

const PrivateRouter: React.FC<IProps> = (_: IProps) => {
    const userData = getLocalStorageValue('authParams');
    const authorities = userData?.user?.authorities ?? [];
    const isUserRole99 = `${userData?.user?.role}` === `${USER_ROLE.MASTER_ADMIN}`;
    const isUserRoleSystemAdmin = `${userData?.user?.role}` === `${USER_ROLE.SYS_ADMIN}`;

    const routeComponentMap = [
        {
            path: ROUTES_PRIVATE.MANAGEMENT.CHILDREN.USER.PATH,
            element: lazy(() => import('../pages/user/UserPage')),
        },
        {
            path: ROUTES_PRIVATE.MANAGEMENT.CHILDREN.CONTAINER_TYPE.PATH,
            element: lazy(() => import('../pages/container-category/ContainerCategoryPage')),
        },
        {
            path: ROUTES_PRIVATE.MANAGEMENT.CHILDREN.CONTENT.PATH,
            element: lazy(() => import('../pages/content/ContentPage')),
        },
        {
            path: ROUTES_PRIVATE.MANAGEMENT.CHILDREN.COMPANY.PATH,
            element: lazy(() => import('../pages/company/CompanyPage')),
        },
        {
            path: ROUTES_PRIVATE.MANAGEMENT.CHILDREN.LOCATION.PATH,
            element: lazy(() => import('../pages/location/LocationPage')),
        },
        {
            path: ROUTES_PRIVATE.MANAGEMENT.CHILDREN.PROCESSING_WORK.PATH,
            element: lazy(() => import('../pages/task/TaskPage')),
        },
        {
            path: ROUTES_PRIVATE.EXTERNAL_API_KEY.PATH,
            element: lazy(() => import('../pages/external-api-key/ExternalApiKeyPage')),
        },
        {
            path: ROUTES_PRIVATE.EXTERNAL_IP_WHITELIST.PATH,
            element: lazy(() => import('../pages/external-ip-whitelist/ExternalIpWhitelistPage')),
        },
        {
            path: ROUTES_PRIVATE.CONTAINER.PATH,
            element: lazy(() => import('../pages/container/ContainerPage')),
        },
        {
            path: ROUTES_PRIVATE.TRACE.PATH,
            element: lazy(() => import('../pages/tracking/Page')),
        },
        {
            path: ROUTES_PRIVATE.INVENTORY.PATH,
            element: lazy(() => import('../pages/inventory-management/InventoryManagementPage')),
        },
        {
            path: ROUTES_PRIVATE.INVENTORY_PROGRESS.PATH,
            element: lazy(() => import('../pages/inventory-progress/Page')),
        },
        {
            path: ROUTES_PRIVATE.INVENTORY_PERFORMANCE.PATH,
            element: lazy(() => import('../pages/inventory-performance/Page')),
        },
        {
            path: ROUTES_PRIVATE.INSTALLATION.PATH,
            element: lazy(() => import('../pages/installation/InstallationPage')),
        },
        {
            path: ROUTES_PRIVATE.RESTOCK.PATH,
            element: lazy(() => import('../pages/receiving-work/ReceivingWorkPage')),
        },
        {
            path: ROUTES_PRIVATE.COLLECTION.PATH,
            element: lazy(() => import('../pages/collection/CollectionPage')),
        },
        {
            path: ROUTES_PRIVATE.PROCESSING.PATH,
            element: lazy(() => import('../pages/processing/ProcessingPage')),
        },
        {
            path: ROUTES_PRIVATE.SHIPPING.PATH,
            element: lazy(() => import('../pages/export/ExportPage')),
        },
        {
            path: ROUTES_PRIVATE.UNIT_INTEGRATION.PATH,
            element: lazy(() => import('../pages/unit-integration/UnitIntegrationPage')),
        },
        {
            path: `${ROUTES_PRIVATE.UNIT_INTEGRATION.PATH}/:taskId`,
            element: lazy(() => import('../pages/unit-integration/UnitIntegrationDetailPage')),
        },
        {
            path: ROUTES_PRIVATE.HISTORY.PATH,
            element: lazy(() => import('../pages/task-history/TaskHistoryPage')),
        },
        {
            path: ROUTES_SUPER_ADMIN.CONTRACT.PATH,
            element: lazy(() => import('../pages/contract/ContractPage')),
        },
        {
            path: ROUTES_PRIVATE.MOVING.PATH,
            element: lazy(() => import('../pages/transport-task/TransportTaskPage')),
        },
        {
            path: ROUTES_PRIVATE.MASTER_HISTORY.PATH,
            element: lazy(() => import('../pages/master-history/MasterHistoryPage')),
        },
        {
            path: ROUTES_PRIVATE.PRINT_SETTING.PATH,
            element: lazy(() => import('../pages/print-setting/PrintSettingPage')),
        },
        {
            path: ROUTES_PRIVATE.DASHBOARD.PATH,
            element: lazy(() => import('../pages/dashboard/DashboardPage')),
        },
        {
            path: ROUTES_PRIVATE.CONTAINER_COLLECTION.PATH,
            element: lazy(() => import('../pages/container-collection/ContainerCollectionPage')),
        },
        {
            path: ROUTES_PRIVATE.PRINT_SETTING.PATH,
            element: lazy(() => import('../pages/print-setting/PrintSettingPage')),
        },
        {
            path: ROUTES_PRIVATE.THRESHOLD_SETTING.PATH,
            element: lazy(() => import('../pages/threshold/ThresholdPage')),
        },
    ];

    const getFirstAuthorizedRoute = () => {
        if (isUserRole99) {
            return lazy(() => import('../pages/contract/ContractPage'));
        }

        if (isUserRoleSystemAdmin) {
            return lazy(() => import('../pages/user/UserPage'));
        }

        const matched = Object.values(ROUTES_PRIVATE).find((route) =>
            authorities.includes(route.ID),
        );

        const matchedPath = matched?.PATH;
        const matchedConfig = routeComponentMap.find((r) => r.path === matchedPath);

        return matchedConfig ? matchedConfig.element : lazy(() => import('./NotFoundPage'));
    };

    const privateRoutes = [
        {
            path: '/',
            element: getFirstAuthorizedRoute(),
        },
        ...routeComponentMap,
        {
            path: '*',
            element: lazy(() => import('./NotFoundPage')),
        },
    ];

    return (
        <>
            <Routes>
                <Route element={<BaseLayout />}>
                    {privateRoutes.map((route, index) => (
                        <Route
                            path={route.path}
                            element={
                                <React.Suspense fallback={<>...</>}>
                                    {React.createElement(route.element)}
                                </React.Suspense>
                            }
                            key={index}
                        />
                    ))}
                </Route>
            </Routes>
        </>
    );
};

export default PrivateRouter;
